import { useEffect, useState } from "react";
import { useAuth } from "@/components/auth/AuthProvider";
import { supabase } from "@/integrations/supabase/client";
import { WaitlistFormValues } from "@/components/waitlist/schema";

export interface MatchableHome {
  county_slug?: string | null;
  county?: string | null;
  fair_deal_approved?: boolean | null;
  inspection_rating?: string | null;
  wheelchair_accessible?: boolean | null;
  specializations?: string[] | null;
  category?: string | null;
  price_range?: string | null;
}

export interface MatchScore {
  score: number;           // 0–100
  label: "Excellent" | "Good" | "Fair" | "Low";
  reasons: string[];       // What matched
  misses: string[];        // What didn't match
}

// Care need → nursing home specializations mapping
const CARE_NEED_TO_SPECIALIZATION: Record<string, string[]> = {
  dementia: ["dementia", "alzheimer", "memory", "cognitive"],
  palliative: ["palliative", "end of life", "hospice"],
  respite: ["respite", "short-term", "short stay"],
  rehabilitation: ["rehabilitation", "rehab", "recovery", "physiotherapy"],
  general: [], // general care matches everything
};

function parseMinPrice(priceRange: string | null | undefined): number | null {
  if (!priceRange) return null;
  const match = priceRange.match(/€([\d,]+)/);
  return match ? parseInt(match[1].replace(",", ""), 10) : null;
}

function getLabel(score: number): MatchScore["label"] {
  if (score >= 80) return "Excellent";
  if (score >= 60) return "Good";
  if (score >= 40) return "Fair";
  return "Low";
}

export function calculateMatchScore(
  home: MatchableHome,
  prefs: WaitlistFormValues
): MatchScore {
  const reasons: string[] = [];
  const misses: string[] = [];
  let totalWeight = 0;
  let earnedWeight = 0;

  // ── 1. County / location match (weight: 30) ──────────────────────────────
  const locationWeight = 30;
  totalWeight += locationWeight;
  const homeCountySlug = home.county_slug || home.county?.toLowerCase().replace(/\s+/g, "-");
  const prefCounties = prefs.preferredLocation || [];
  if (prefCounties.length === 0 || prefCounties.includes(homeCountySlug || "")) {
    earnedWeight += locationWeight;
    if (prefCounties.length > 0) reasons.push("In your preferred county");
  } else {
    misses.push("Outside your preferred county");
  }

  // ── 2. Care needs / specializations match (weight: 25) ───────────────────
  const careWeight = 25;
  totalWeight += careWeight;
  const userCareNeeds = prefs.careNeeds || [];
  const homeSpecs = (home.specializations || []).map((s) => s.toLowerCase());

  if (userCareNeeds.length === 0) {
    earnedWeight += careWeight; // no filter = full score
  } else {
    const isGeneral = userCareNeeds.includes("general");
    const hasSpecialized = userCareNeeds.filter((n) => n !== "general");

    if (isGeneral && hasSpecialized.length === 0) {
      // Only general care selected — all homes match
      earnedWeight += careWeight;
      reasons.push("Offers general care");
    } else {
      const matchedNeeds = hasSpecialized.filter((need) => {
        const keywords = CARE_NEED_TO_SPECIALIZATION[need] || [need];
        return keywords.some((kw) => homeSpecs.some((s) => s.includes(kw)));
      });

      if (matchedNeeds.length > 0) {
        earnedWeight += careWeight * (matchedNeeds.length / hasSpecialized.length);
        reasons.push(`Offers ${matchedNeeds.map((n) => n.charAt(0).toUpperCase() + n.slice(1)).join(", ")} care`);
      } else if (hasSpecialized.length > 0) {
        misses.push("Specialist care not confirmed");
      }
    }
  }

  // ── 3. Fair Deal match (weight: 20) ──────────────────────────────────────
  const fairDealWeight = 20;
  totalWeight += fairDealWeight;
  if (prefs.fairDealScheme === "applied" || prefs.fairDealScheme === "need-info") {
    if (home.fair_deal_approved === true) {
      earnedWeight += fairDealWeight;
      reasons.push("Fair Deal approved");
    } else if (home.fair_deal_approved === false) {
      misses.push("Not Fair Deal approved");
    } else {
      earnedWeight += fairDealWeight * 0.5; // unknown — partial
    }
  } else {
    earnedWeight += fairDealWeight; // not relevant to user
  }

  // ── 4. HIQA rating (weight: 15) ──────────────────────────────────────────
  const hiqaWeight = 15;
  totalWeight += hiqaWeight;
  const rating = (home.inspection_rating || "").toLowerCase();
  if (rating.includes("compliant") || rating.includes("substantially")) {
    earnedWeight += hiqaWeight;
    if (rating.includes("compliant") && !rating.includes("substantially")) {
      reasons.push("HIQA Compliant rating");
    } else {
      reasons.push("HIQA Substantially Compliant");
    }
  } else if (rating) {
    misses.push("Lower HIQA rating");
  } else {
    earnedWeight += hiqaWeight * 0.5; // unrated — partial
  }

  // ── 5. Mobility / wheelchair (weight: 10) ─────────────────────────────────
  const mobilityWeight = 10;
  totalWeight += mobilityWeight;
  const needsWheelchair =
    (prefs.mobilityRequirements || "").toLowerCase().includes("wheelchair") ||
    (prefs.mobilityRequirements || "").toLowerCase().includes("mobility");

  if (!needsWheelchair) {
    earnedWeight += mobilityWeight; // not relevant
  } else if (home.wheelchair_accessible === true) {
    earnedWeight += mobilityWeight;
    reasons.push("Wheelchair accessible");
  } else if (home.wheelchair_accessible === false) {
    misses.push("Wheelchair access not confirmed");
  } else {
    earnedWeight += mobilityWeight * 0.5; // unknown
  }

  const score = Math.round((earnedWeight / totalWeight) * 100);
  return { score, label: getLabel(score), reasons, misses };
}

// Hook: fetches user prefs once and exposes a scorer function
export function useMatchScore() {
  const { user } = useAuth();
  const [prefs, setPrefs] = useState<WaitlistFormValues | null>(null);

  useEffect(() => {
    if (!user) return;
    supabase
      .from("profiles")
      .select("saved_preferences")
      .eq("id", user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (data?.saved_preferences) {
          setPrefs(data.saved_preferences as WaitlistFormValues);
        }
      });
  }, [user]);

  const getScore = (home: MatchableHome): MatchScore | null => {
    if (!prefs) return null;
    return calculateMatchScore(home, prefs);
  };

  return { prefs, getScore };
}
